import { Cushion, Grid, MaxWidth, Rectangle } from '@pitchero/react-ui';
import { get, isEmpty } from 'lodash';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React from 'react';
import { useRouter } from 'next/router';
import RosBottomMpu from '../adverts/ros-bottom-mpu';
import ArticleLoadingPlaceHolder from '../article/loading-placeholder';
import AccessDenied from '../auth/access-denied';
import PhotoGrid from '../media/photo-grid';
import RelatedContent from '../related-content';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import TEAM_PROP_TYPES from '../../lib/prop-types/team';
import useBreakpoint from '../../lib/use-breakpoint';
import clubUrl from '../../lib/routing/club-url';
import LineupLoadingState from './lineup-loading';
import LiveUpdateBar from './live-update-bar';
import MatchDetailsLoadingState from './match-details-loading';
import PhotosLoadingState from './photos-loading';
import ScoreCardLoadingState from './scorecard-loading-state';
import ScorecardPlaceholder from './scorecard-placeholder';
import MatchCentrePageTitle from './title';
import Header from './header';
import HeaderLoadingState from './header-loading';
import MatchDetails from './match-details';

const Events = dynamic(() => import('./events'));
const LineUp = dynamic(() => import('./lineup'));
const MatchOverview = dynamic(() => import('./overview'));
const MatchReport = dynamic(() => import('./report'));
const Scorecard = dynamic(() => import('./scorecard'), {
  loading: () => <ScoreCardLoadingState />,
});
const Table = dynamic(() => import('./table'));

const MatchCentre = ({
  album,
  activeSection,
  club,
  clubId,
  competitionRoundup,
  eventsByPeriod,
  fixtureId,
  formGuide,
  headToHead,
  image,
  isLoading,
  lineup,
  location,
  overview,
  playerStats,
  preview,
  report,
  scorecard,
  table,
  team,
  teamId,
  videos,
}) => {
  let content;
  const isLive = get(overview, 'isLive', false);
  const isMobile = useBreakpoint();
  const router = useRouter();
  if (activeSection === 'report') {
    content = isLoading ? (
      <ArticleLoadingPlaceHolder />
    ) : (
      <MatchReport
        clubId={clubId}
        report={report}
        image={image}
        team={team}
        album={album}
        shareUrl={clubUrl(club, 'team.match_centre.report', { teamId, fixtureId })}
      />
    );
  } else if (activeSection === 'lineup') {
    if (isLoading) {
      content = <LineupLoadingState />;
    } else {
      content =
        lineup && lineup.accessDenied ? (
          <AccessDenied clubId={clubId} messageTranslationKey="teams:lineup_access_denied" />
        ) : (
          <LineUp {...lineup} overview={overview} teamId={teamId} teamName={team.name} />
        );
    }
  } else if (activeSection === 'table') {
    content = <Table table={table} isMobile={isMobile} />;
  } else if (activeSection === 'events') {
    content = (
      <Events
        eventsByPeriod={eventsByPeriod}
        homeOrAway={overview?.ha || null}
        isLive={isLive}
        overview={overview}
      />
    );
  } else if (activeSection === 'scorecard') {
    if (isLoading) {
      content = <ScoreCardLoadingState />;
    } else if (scorecard) {
      content = <Scorecard overview={overview} scorecard={scorecard} teamId={teamId} />;
    } else {
      content = <ScorecardPlaceholder />;
    }
  } else if (activeSection === 'photos') {
    if (isLoading) {
      content = <PhotosLoadingState />;
    } else {
      content =
        album && album.accessDenied ? (
          <AccessDenied
            clubId={clubId}
            messageTranslationKey="teams:photos_access_denied"
            onAuthorise={() => {
              router.reload();
            }}
          />
        ) : (
          album && (
            <PhotoGrid
              albumId={album.id}
              fixtureId={fixtureId}
              images={album.images}
              teamId={teamId}
              withAds={false}
            />
          )
        );
    }
  } else {
    content = isLoading ? (
      <ArticleLoadingPlaceHolder noImage />
    ) : (
      <MatchOverview
        competitionRoundup={competitionRoundup}
        headToHead={headToHead}
        location={location}
        overview={overview}
        playerStats={playerStats}
        preview={preview}
        teamId={teamId}
        videos={videos}
        shareProps={{
          url: clubUrl(club, 'team.match_centre.overview', { teamId, fixtureId }),
          imageUrl: image,
        }}
      />
    );
  }

  const matchDetails =
    isLoading || !overview ? (
      <MatchDetailsLoadingState />
    ) : (
      <MatchDetails
        overview={overview}
        table={table}
        location={location}
        teamId={teamId}
        club={club}
      />
    );
  const header =
    isLoading || !overview ? (
      <HeaderLoadingState />
    ) : (
      <Header
        overview={overview}
        location={location}
        club={club}
        teamId={teamId}
        teamName={team.name}
        fixtureId={fixtureId}
        activeSection={activeSection}
        formGuide={formGuide}
        hasEvents={!isEmpty(eventsByPeriod)}
        hasLineup={!!lineup}
        hasPhotos={!!album}
        hasReport={!!report}
        hasScorecard={!!scorecard}
        hasTable={!!table}
        image={image}
        scoreCard={scorecard}
        isMobile={isMobile}
      />
    );

  const rightHandRail = (
    <Cushion top="medium">
      <RosBottomMpu uid={`${fixtureId}-${activeSection}`} />
    </Cushion>
  );

  // Can't rely on match report being available all the time,
  // so fallback to the club's related content when not present on
  // See: https://pitchero.atlassian.net/browse/WEB-5067
  const relatedNews = () => {
    if (activeSection !== 'overview' && activeSection !== 'report') return null;

    const wrapRelated = (related) => (
      <>
        {isMobile ? (
          <Cushion horizontal="small" component="div">
            {related}
          </Cushion>
        ) : (
          <Rectangle
            borderTop="1px solid #d9d9d9"
            style={{ paddingTop: '30px', marginTop: '20px' }}
            component="div"
          >
            {related}
          </Rectangle>
        )}
      </>
    );

    return wrapRelated(
      <RelatedContent
        clubId={clubId}
        key={activeSection}
        messageId={activeSection === 'overview' ? `team-${teamId}` : `match_report-${report?.id}`}
      />,
    );
  };

  const v2Sections = ['overview', 'report', 'table', 'photos', 'events', 'lineup'];

  return (
    <>
      <MatchCentrePageTitle
        activeSection={activeSection}
        fixtureId={fixtureId}
        overview={overview}
        team={team}
      />
      {header}
      <Cushion
        all="large"
        responsive={[{ maxWidth: 'fullHeader', props: { horizontal: '0' } }]}
        style={{ backgroundColor: v2Sections.includes(activeSection) ? 'white' : 'transparent' }}
        component="div"
      >
        {isMobile ? (
          <Grid columns="1fr" rowGap="20px" style={{ gridAutoRows: 'auto' }}>
            {content}
            <div style={{ order: activeSection === 'overview' ? -1 : 0 }}>{matchDetails}</div>
            {rightHandRail}
            {relatedNews()}
          </Grid>
        ) : (
          <MaxWidth component="div" maxWidth={1366} center>
            <Grid
              columns="auto minmax(0, 380px)"
              rowGap="medium"
              columnGap="medium"
              responsive={[{ maxWidth: 'fullHeader', props: { columns: '1fr' } }]}
            >
              <div>
                {isLive && <LiveUpdateBar clubId={clubId} fixtureId={fixtureId} teamId={teamId} />}
                {content}
              </div>
              <div>
                {matchDetails}
                {rightHandRail}
              </div>
            </Grid>
            {relatedNews()}
          </MaxWidth>
        )}
      </Cushion>
    </>
  );
};

MatchCentre.defaultProps = {
  album: null,
  competitionRoundup: [],
  eventsByPeriod: null,
  formGuide: null,
  headToHead: [],
  image: null,
  lineup: null,
  location: null,
  overview: null,
  playerStats: [],
  preview: null,
  report: null,
  scorecard: null,
  table: null,
  videos: [],
};

MatchCentre.propTypes = {
  album: PropTypes.shape(),
  activeSection: PropTypes.oneOf([
    'overview',
    'report',
    'lineup',
    'table',
    'photos',
    'scorecard',
    'events',
  ]).isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  clubId: PropTypes.number.isRequired,
  competitionRoundup: PropTypes.arrayOf(PropTypes.shape()),
  eventsByPeriod: PropTypes.arrayOf(PropTypes.shape()),
  formGuide: PropTypes.shape(),
  headToHead: PropTypes.arrayOf(PropTypes.shape()),
  image: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  lineup: PropTypes.shape(),
  location: PropTypes.shape(),
  overview: PropTypes.shape(),
  playerStats: PropTypes.arrayOf(PropTypes.shape()),
  preview: PropTypes.shape(),
  report: PropTypes.shape(),
  scorecard: PropTypes.shape(),
  table: PropTypes.shape(),
  fixtureId: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  team: TEAM_PROP_TYPES.isRequired,
  videos: PropTypes.arrayOf(PropTypes.shape()),
};

export default MatchCentre;
